import type {SegmentationModel} from './types/segmentation';
import {SEGMENTATION_MODELS} from './types/segmentation';
import type {ImageMapIterable, ImageRecord} from './types/media';
import type {RenderingEvents} from './constants';
import {RENDERING_EVENTS} from './constants';
import type {RenderEventHandlers} from './types/render';
import type {Stats, SelectionOptions} from './types/utils';

export const isSegmentationModel = (t: unknown): t is SegmentationModel => {
    if (
        typeof t === 'string' &&
        Object.values(SEGMENTATION_MODELS).includes(t as SegmentationModel)
    ) {
        return true;
    }
    return false;
};

export const isRenderingEvents = (t: unknown): t is RenderingEvents => {
    if (
        typeof t === 'string' &&
        Object.values(RENDERING_EVENTS).includes(t as RenderingEvents)
    ) {
        return true;
    }
    return false;
};

export const toRenderingEvents = (t: unknown): RenderingEvents => {
    if (isRenderingEvents(t)) {
        return t;
    }
    return RENDERING_EVENTS.Error;
};

export const getErrorMessage = (error: unknown) =>
    error instanceof Error ? error.message : String(error);

export const createObjectUpdater = <T extends {[K in keyof T]: unknown}>(
    original: T,
) => {
    const keys = new Set(Object.keys(original));
    return (another: Partial<T>) => {
        for (const key in another) {
            if (keys.has(key) && typeof original[key] === typeof another[key]) {
                original[key] = another[key] as T[typeof key];
            }
        }
    };
};

export const ImageFetchError = 'ImageFetchError';

const isLikelyImageContentType = (ct: string | null) => {
    if (!ct) {
        return false;
    }
    // handle e.g. "image/png; charset=binary"
    return ct.toLowerCase().startsWith('image/');
};

export const createRemoteImageBitmap = (map?: ImageMapIterable) => {
    const cache = new Map(map);
    const fetchingCache = new Set<string>();

    const fetchImage = async (uri: string, abortSignal: AbortSignal) => {
        const response = await fetch(uri, {signal: abortSignal});
        if (
            response.ok &&
            isLikelyImageContentType(response.headers.get('content-type'))
        ) {
            const blob = await response.blob();
            const image = await createImageBitmap(blob);
            return image;
        }
    };

    const tryFetchingImage = async (uri: string, abortSignal: AbortSignal) => {
        const image = cache.get(uri);
        if (!image) {
            if (fetchingCache.has(uri)) {
                return null;
            }
            fetchingCache.add(uri);
            try {
                const image = await fetchImage(uri, abortSignal);
                if (image) {
                    cache.set(uri, image);
                    return image;
                }
            } catch (error: unknown) {
                if (error instanceof Error) {
                    if (error.name !== 'AbortError') {
                        throw new Error(ImageFetchError, {cause: error});
                    }
                    return;
                }
                throw error;
            } finally {
                fetchingCache.delete(uri);
            }
        }
        return image;
    };

    const removeImage = (uri: string) => {
        const image = cache.get(uri);
        image?.close();
        return cache.delete(uri);
    };

    return {
        tryFetchingImage,
        setImage: (uri: string, image: ImageBitmap) => {
            if (cache.has(uri)) {
                removeImage(uri);
            }
            cache.set(uri, image);
        },
        getImage: (uri: string) => {
            return cache.get(uri) ?? null;
        },
        removeImage,
        clear: () => {
            for (const img of cache.values()) {
                img.close();
            }
            cache.clear();
        },
    };
};

export const clamping = (min: number, max: number) => (value: number) =>
    Math.min(Math.max(value, min), max);

export const calculateMaxBlurPass = (height: number) =>
    Math.trunc(Math.log2(height));

/**
 * Coerce any positive number to the nearest power of two within [minPow, maxPow]
 * Example: nearestPowerOfTwo(13) => 16; nearestPowerOfTwo(3.2) => 4
 */
export function nearestPowerOfTwo(
    n: number,
    minPow: number = 0, // 2^0 = 1
    maxPow: number = 4, // 2^4 = 16
): number {
    if (n <= 0) {
        return 1;
    }
    const k = clamping(minPow, maxPow)(calculateMaxBlurPass(n));
    return 2 ** k;
}

/**
 * Calculate the coordinates and size of the source and destination to fit the
 * target aspect ratio.
 *
 * @param fromWidth - The width of the source
 * @param fromHeight - The height of the source
 * @param toWidth - The width of the destination
 * @param toHeight - The height of the destination
 */
export const resize = (
    fromWidth: number,
    fromHeight: number,
    toWidth: number,
    toHeight: number,
    // eslint-disable-next-line max-params -- avoid unnecessary object creation
) => {
    if (
        !fromWidth ||
        !fromHeight ||
        !toWidth ||
        !toHeight ||
        fromWidth * toHeight === toWidth * fromHeight
    ) {
        return {
            sx: 0,
            sy: 0,
            sw: fromWidth,
            sh: fromHeight,
            dx: 0,
            dy: 0,
            dw: toWidth,
            dh: toHeight,
        };
    }
    const originalAspectRatio = fromWidth / fromHeight;
    const targetAspectRatio = toWidth / toHeight;
    if (originalAspectRatio > targetAspectRatio) {
        const sw =
            Math.ceil(fromWidth * targetAspectRatio) / originalAspectRatio;
        const delta = Math.abs(fromWidth - sw);
        const sx = Math.ceil(delta / 2);
        return {
            sx,
            sy: 0,
            sw,
            sh: fromHeight,
            dx: 0,
            dy: 0,
            dw: toWidth,
            dh: toHeight,
        };
    }
    const sh = Math.ceil(fromHeight * originalAspectRatio) / targetAspectRatio;
    const delta = Math.abs(fromHeight - sh);
    const sy = Math.ceil(delta / 2);
    return {
        sx: 0,
        sy,
        sw: fromWidth,
        sh,
        dx: 0,
        dy: 0,
        dw: toWidth,
        dh: toHeight,
    };
};

export const createLazyProps = <
    T extends Record<string, unknown>,
>(stateBuilder: {
    [K in keyof T]: (s: Partial<T>) => T[K];
}) => {
    const props: Partial<T> = {};
    return new Proxy(
        props as T,
        {
            get(target, p: keyof T, receiver) {
                const prop = target[p];
                if (!prop) {
                    target[p] = stateBuilder[p]?.(props);
                }
                return Reflect.get(target, p, receiver) as T[typeof p];
            },
        } as ProxyHandler<T>,
    );
};

export const handleWebGLContextLoss = (
    canvas: OffscreenCanvas,
    eventHandlers: RenderEventHandlers,
    contextLostCb?: () => void,
) => {
    const handleContextLost = (event: WebGLContextEvent) => {
        event.preventDefault();
        contextLostCb?.();
        eventHandlers.contextLost(event.statusMessage);
    };
    const handleContextRestored = (event: WebGLContextEvent) => {
        eventHandlers.contextRestored(event.statusMessage);
    };
    const handleContextCreationError = (event: WebGLContextEvent) => {
        eventHandlers.contextCreationError(event.statusMessage);
    };

    canvas.addEventListener('webglcontextlost', handleContextLost);
    canvas.addEventListener('webglcontextrestored', handleContextRestored);
    canvas.addEventListener(
        'webglcontextcreationerror',
        handleContextCreationError,
    );

    return () => {
        canvas.removeEventListener('webglcontextlost', handleContextLost);
        canvas.removeEventListener(
            'webglcontextrestored',
            handleContextRestored,
        );
        canvas.removeEventListener(
            'webglcontextcreationerror',
            handleContextCreationError,
        );
    };
};

/**
 * Intersection over Union (IoU), how much two masks overlap as a fraction of either's total.
 * 1.0 = perfect match, 0 = no overlap.
 * Used for stable, robust tracking of the main person between frames,
 * especially with bystander removal and temporal instance switching.
 */
export function iou(a: Uint8Array, b: Uint8Array): number {
    let inter = 0;
    let union = 0;
    for (let i = 0; i < a.length; i++) {
        // @ts-expect-error TypeArray will not be undefined
        const av = a[i] >= 128;
        // @ts-expect-error TypeArray will not be undefined
        const bv = b[i] >= 128;
        if (av && bv) {
            inter++;
        }
        if (av || bv) {
            union++;
        }
    }
    return union ? inter / union : 0;
}

export function labelComponents(
    input: Uint8Array,
    labels: Int32Array,
    width: number,
    height: number,
) {
    // 8-connected component labeling with union-find
    let nextLabel = 1;
    const parent: number[] = [0];

    function find(x: number): number {
        if (parent[x] === undefined) {
            return x;
        }
        if (parent[x] !== x) {
            parent[x] = find(parent[x]);
        }
        return parent[x];
    }

    function unite(a: number, b: number) {
        if (!a || !b) {
            return;
        }
        a = find(a);
        b = find(b);
        if (a !== b) {
            parent[b] = a; // Or vice versa, as long as consistent
        }
    }

    // First pass: assign and union with neighbors (left, top, top-left, top-right)
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = y * width + x;
            //@ts-expect-error will not be undefined
            if (input[i] < 128) {
                labels[i] = 0;
                continue;
            }

            const left = x > 0 ? labels[i - 1] : 0;
            const top = y > 0 ? labels[i - width] : 0;
            const topLeft = x > 0 && y > 0 ? labels[i - width - 1] : 0;
            const topRight = x < width - 1 && y > 0 ? labels[i - width + 1] : 0;

            // Pick an existing neighbor label if present, else new
            let assign = left || top || topLeft || topRight;
            if (!assign) {
                assign = nextLabel++;
                parent[assign] = assign;
            }
            labels[i] = assign;

            // Union all differing neighbors with the assigned label
            if (left && left !== assign) {
                unite(assign, left);
            }
            if (top && top !== assign) {
                unite(assign, top);
            }
            if (topLeft && topLeft !== assign) {
                unite(assign, topLeft);
            }
            if (topRight && topRight !== assign) {
                unite(assign, topRight);
            }
        }
    }

    // Second pass: compress labels to root ids
    for (let i = 0; i < labels.length; i++) {
        const l = labels[i];
        labels[i] = l ? find(l) : 0;
    }

    // Compute component stats for selection
    const stats = new Map<number, Stats>();
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const l = labels[y * width + x];
            if (!l) {
                continue;
            }
            const s = stats.get(l) ?? {
                sumX: 0,
                sumY: 0,
                count: 0,
                minX: width,
                minY: height,
                maxX: -1,
                maxY: -1,
            };
            s.sumX += x;
            s.sumY += y;
            s.count++;
            if (x < s.minX) {
                s.minX = x;
            }
            if (x > s.maxX) {
                s.maxX = x;
            }
            if (y < s.minY) {
                s.minY = y;
            }
            if (y > s.maxY) {
                s.maxY = y;
            }
            stats.set(l, s);
        }
    }
    return stats;
}

export function scoreComponent(
    stats: Stats,
    overlap: number,
    weights: SelectionOptions,
    width: number,
    height: number,
) {
    const area = stats.count;
    const heightPx = stats.maxY - stats.minY + 1;
    // const widthPx = stats.maxX - stats.minX + 1;
    const mx = stats.sumX / stats.count;
    const my = stats.sumY / stats.count;
    const d2 = (mx - weights.centerX) ** 2 + (my - weights.centerY) ** 2;
    const centerScore =
        1.0 - Math.min(1.0, Math.sqrt(d2) / Math.max(width, height)); // normalize

    // Normalize area/height by frame size to [0..1]
    const areaN = area / (width * height);
    const heightN = heightPx / height;

    const score =
        weights.iou * overlap +
        weights.area * areaN +
        weights.height * heightN +
        weights.center * centerScore;
    return score;
}

export const cloneImageRecord = async (image: ImageRecord) => {
    const cloned = await createImageBitmap(image.image);
    return {image: cloned, key: image.key};
};

/**
 * Compare provided ImageRecord
 *
 * @returns true if the two records have the same key and image, false otherwise
 */
export const compareImageRecords = (
    a: ImageRecord,
    b: ImageRecord | undefined,
) => {
    if (!b) {
        return false;
    }
    if (a.key !== b.key || a.image !== b.image) {
        return false;
    }
    return true;
};
